'use client';

import { StudioAction } from '@/components/ui/StudioAction';

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { ArrowRight, Check, CircleDollarSign, Gift, X } from 'lucide-react';
import { SystemLoadingIcon } from '@/components/ui/SystemLoading';
import { useBodyScrollLock } from '@/lib/hooks/useBodyScrollLock';
import { welcomeRewardApi } from '@/lib/api/welcome-reward';
import { isWelcomeRewardSurface, shouldAutoShowWelcomeReward } from '@/lib/credits/welcome-reward';
import { observeWelcomeVisibility } from '@/lib/credits/welcome-visibility';
import { getAuthSessionVersion, useAuthStore } from '@/stores/authStore';
import { useUserProfileStore } from '@/stores/userProfileStore';
import { useCreditsStore } from '@/lib/stores/credits-store';
import { useCreditsJourneyStore } from '@/lib/stores/credits-journey-store';
import { useCreditsJourneyAccount } from './useCreditsJourneyAccount';
import styles from './WelcomeReward.module.css';

const OWNER = 'welcome-reward';

/** The durable wallet benefit is also the reward's inbox record. Reading it
 * never changes eligibility; only the atomic claim turns pending into granted. */
export function WelcomeRewardMessage() {
  const t = useTranslations('welcomeReward');
  const credits = useCreditsStore((state) => state.credits);
  const ready = useCreditsJourneyStore((state) => state.ready);
  const status = credits?.welcome_credit_status;
  if (status !== 'pending' && status !== 'granted') return null;
  const pending = status === 'pending';
  return (
    <div data-welcome-reward-message className="rounded-[10px] border border-[#CBEAF6] bg-[#F1FAFE] p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-[#173349]">
        <Gift className="h-4 w-4 text-[#1686AD]" aria-hidden />
        <span>{t('messageTitle')}</span>
        {pending && <span data-welcome-unread className="ml-auto h-2 w-2 rounded-full bg-[#4ABDE9]" aria-label={t('unclaimed')} />}
      </div>
      <p className="mt-2 text-xs leading-5 text-[#526D7F]">{t(pending ? 'messageBody' : 'messageClaimed')}</p>
      {pending ? <StudioAction variant="primary" type="button" disabled={!ready}
        onClick={() => void useCreditsJourneyStore.getState().openWelcome(OWNER)}
        className="mt-3 gap-2">
        {t('claim')}
      </StudioAction> : <span className="mt-3 inline-flex items-center gap-1 text-xs text-[#247B64]"><Check className="h-4 w-4" aria-hidden />{t('claimed')}</span>}
    </div>
  );
}

export function WelcomeRewardHost() {
  useCreditsJourneyAccount();
  const pathname = usePathname() || '';
  const userId = useAuthStore((state) => state.isAuthenticated ? state.user?.id : undefined);
  const profile = useUserProfileStore((state) => state.profile);
  const credits = useCreditsStore((state) => state.credits);
  const ready = useCreditsJourneyStore((state) => state.ready);
  const shown = useCreditsJourneyStore((state) => state.welcomeShown);
  const owner = useCreditsJourneyStore((state) => state.welcomeOwner);
  const accountId = useCreditsJourneyStore((state) => state.userId);
  const markVisible = useCallback(() => {
    if (userId) void useCreditsJourneyStore.getState().markWelcomeVisible(OWNER, userId);
  }, [userId]);

  // OnboardingGate owns the profile request and its retry UI. A second global
  // fetch would race its auth-scoped load on initial sign-in/navigation.
  const eligibleSurface = isWelcomeRewardSurface(pathname, userId, profile);
  useEffect(() => {
    if (!shouldAutoShowWelcomeReward(eligibleSurface, ready, accountId, userId, shown, credits)) return;
    void useCreditsJourneyStore.getState().openWelcome(OWNER, true);
  }, [eligibleSurface, ready, accountId, userId, shown, credits]);

  if (!eligibleSurface || accountId !== userId || owner !== OWNER || !credits
    || !['pending', 'granted'].includes(credits.welcome_credit_status || '')) return null;
  return <WelcomeRewardDialog key={userId} onVisible={markVisible} />;
}

export function WelcomeRewardDialog({onVisible}: {onVisible?: () => void} = {}) {
  const t = useTranslations('welcomeReward');
  const panel = useRef<HTMLDivElement>(null);
  const inFlight = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const credits = useCreditsStore((state) => state.credits);
  const claimed = credits?.welcome_credit_status === 'granted';
  const close = useCallback(() => useCreditsJourneyStore.getState().hideWelcome(), []);
  useBodyScrollLock(true);
  useEffect(() => {
    if (onVisible && panel.current) return observeWelcomeVisibility(panel.current, onVisible);
  }, [onVisible]);

  useEffect(() => {
    if (!claimed) return;
    panel.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const timer = window.setTimeout(close, 1200);
    return () => window.clearTimeout(timer);
  }, [claimed, close]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const dialog = panel.current;
    const focusable = () => Array.from(dialog?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') || []);
    focusable()[0]?.focus();
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
      if (event.key !== 'Tab') return;
      const items = focusable();
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog?.contains(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog?.contains(document.activeElement))) {
        event.preventDefault(); first?.focus();
      }
    }
    document.addEventListener('keydown', handleKey, true);
    return () => { document.removeEventListener('keydown', handleKey, true); if (previous?.isConnected) previous.focus(); };
  }, [close]);

  async function claim() {
    if (inFlight.current || claimed) return;
    const userId = useAuthStore.getState().user?.id;
    const session = getAuthSessionVersion();
    const isCurrent = () => userId === useAuthStore.getState().user?.id && session === getAuthSessionVersion();
    inFlight.current = true; setLoading(true); setError(false);
    try {
      const response = await welcomeRewardApi.claim();
      if (!isCurrent()) return;
      if (!response.success || response.error || response.data?.credits.welcome_credit_status !== 'granted') throw new Error('claim_failed');
      useCreditsStore.getState().setCredits(response.data.credits);
      setCelebrate(!response.data.already_claimed);
    } catch {
      if (isCurrent()) setError(true);
    } finally {
      inFlight.current = false;
      if (isCurrent()) setLoading(false);
    }
  }

  return createPortal(
    <div className={styles.overlay} onClick={(event) => { if (event.target === event.currentTarget) close(); }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="welcome-reward-title" aria-describedby="welcome-reward-description"
        data-welcome-reward className={`${styles.card} ${celebrate ? styles.claimed : ''}`}>
        {/* Assets and choreography supplied in DobiDobi-Welcome-Reward(1).html. */}
        {/* eslint-disable @next/next/no-img-element */}
        <img className={styles.surface} src="/welcome-reward/surface.webp" alt="" />
        <div className={styles.body}>
          <button type="button" className={styles.close} aria-label={t('close')} onClick={close}><X size={20} /></button>
          <img className={styles.illustration} src="/welcome-reward/envelope.webp" alt="" />
          <h2 id="welcome-reward-title" className={styles.title}>{t(claimed ? 'successTitle' : 'title')}</h2>
          <p id="welcome-reward-description" className={styles.welcome}>{t(claimed ? 'successBody' : 'welcome')}</p>
          <div className={styles.amount}><strong>500</strong><span>{t('unit')}</span><CircleDollarSign strokeWidth={1.8} aria-hidden /></div>
          <StudioAction variant="primary" type="button" className={styles.claim} disabled={loading || claimed} onClick={() => void claim()} aria-busy={loading}>
            {loading && <SystemLoadingIcon className="h-5 w-5" />}
            <span>{t(loading ? 'claiming' : claimed ? 'successButton' : error ? 'retry' : 'claim')}</span>
            {!loading && !claimed && <ArrowRight className="h-5 w-5" aria-hidden />}
          </StudioAction>
          {celebrate && Array.from({ length: 9 }, (_, i) => <img key={i} className={styles.particle} src="/welcome-reward/coin.webp" alt=""
            style={{ '--x': `${(i - 4) * 24}px`, '--y': `${-55 - Math.sin(i / 8 * Math.PI) * 72}px`, '--r': `${(i - 4) * 18}deg`, '--delay': `${i * .025}s` } as CSSProperties} />)}
        </div>
        {error && <p className={styles.error} role="alert">{t('error')}</p>}
        <p role="status" className="sr-only">{claimed ? t('successButton') : loading ? t('claiming') : ''}</p>
      </div>
    </div>, document.body,
  );
}
